import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { agentMemoryApi } from "../services/api";
import { useAgentPermissions } from "../context/AgentPermissionsContext";
import { useNotification } from "../context/NotificationContext";
import { detailText } from "../utils/apiErrors";

// Long-term memory the AI Technician may be shown about ONE customer (MEM-LONGTERM-001).
// Everything here is a person acting: the backend attributes each change to the signed-in user and
// answers 404 for a note of another customer. Reading needs agents_view, changing needs agents_manage.

export const CLASS_LABELS = {
  VERIFIED_CUSTOMER_FACT: "עובדה על הלקוח",
  VERIFIED_ENVIRONMENT_FACT: "עובדה על הסביבה",
  COMMUNICATION_PREFERENCE: "העדפת תקשורת",
  TECHNICIAN_FEEDBACK: "משוב טכנאי",
  RECURRING_PATTERN: "דפוס חוזר",
  TEMPORARY_CONTEXT: "הקשר זמני (פג תוך 7 ימים)",
  RESOLUTION_OUTCOME: "תוצאת פתרון",
};
// Classes a person may add by hand here (a resolution outcome needs structured evidence fields).
const ADDABLE_CLASSES = Object.keys(CLASS_LABELS).filter((c) => c !== "RESOLUTION_OUTCOME");

export const STATUS_LABELS = {
  ACTIVE: "מאומת",
  UNVERIFIED: "לא מאומת",
  STALE: "ישן — כדאי לאמת מחדש",
  CONFLICTED: "סותר הערה אחרת",
  DEPRECATED: "הוצא משימוש",
  EXPIRED: "פג תוקף",
};
const STATUS_STYLES = {
  ACTIVE: "bg-green-100 text-green-700",
  UNVERIFIED: "bg-amber-100 text-amber-700",
  STALE: "bg-amber-100 text-amber-700",
  CONFLICTED: "bg-red-100 text-red-700",
  DEPRECATED: "bg-gray-100 text-gray-500",
  EXPIRED: "bg-gray-100 text-gray-500",
};

const kindFor = (memoryClass) => (memoryClass === "COMMUNICATION_PREFERENCE" ? "PREFERENCE" : "FACT");

function NoteRow({ note, canManage, verifyBusy, correctBusy, deprecateBusy, deleteBusy, onVerify, onCorrect, onDeprecate, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(note.content);
  const live = note.status !== "DEPRECATED";

  return (
    <li className="border rounded-lg p-3 space-y-2 bg-white" data-testid="memory-note">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className={`px-2 py-0.5 rounded-full font-medium ${STATUS_STYLES[note.status] || STATUS_STYLES.DEPRECATED}`}>
          {STATUS_LABELS[note.status] || note.status}
        </span>
        <span className="text-gray-500">{CLASS_LABELS[note.memory_class] || note.memory_class}</span>
        {note.verified_by && <span className="text-gray-400">אומת ע"י {note.verified_by}</span>}
      </div>

      {editing ? (
        <textarea
          rows={3}
          dir="auto"
          value={text}
          onChange={(e) => setText(e.target.value)}
          aria-label="תוכן מתוקן"
          className="w-full border rounded-lg px-3 py-2 text-sm"
        />
      ) : (
        <p className="text-sm text-gray-800 whitespace-pre-wrap" dir="auto">{note.content}</p>
      )}
      {note.caveat && !editing && <p className="text-xs text-amber-700">{note.caveat}</p>}

      {canManage && live && (
        <div className="flex flex-wrap gap-2">
          {editing ? (
            <>
              <button
                type="button"
                disabled={correctBusy || !text.trim()}
                onClick={() => onCorrect(note, text.trim(), () => setEditing(false))}
                className="bg-brand-600 hover:bg-brand-700 text-white px-3 py-1 rounded text-xs disabled:opacity-50"
              >
                שמור תיקון
              </button>
              <button type="button" onClick={() => { setEditing(false); setText(note.content); }} className="border px-3 py-1 rounded text-xs">
                ביטול
              </button>
            </>
          ) : (
            <>
              {(note.status === "UNVERIFIED" || note.status === "STALE") && (
                <button type="button" disabled={verifyBusy} onClick={() => onVerify(note)} className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-xs disabled:opacity-50">
                  אמת
                </button>
              )}
              <button type="button" onClick={() => setEditing(true)} className="border px-3 py-1 rounded text-xs hover:bg-gray-50">
                תקן
              </button>
              <button type="button" disabled={deprecateBusy} onClick={() => onDeprecate(note)} className="border px-3 py-1 rounded text-xs hover:bg-gray-50 disabled:opacity-50">
                הוצא משימוש
              </button>
              <button type="button" disabled={deleteBusy} onClick={() => onDelete(note)} className="border border-red-200 text-red-600 px-3 py-1 rounded text-xs hover:bg-red-50 disabled:opacity-50">
                מחק
              </button>
            </>
          )}
        </div>
      )}
    </li>
  );
}

export default function CustomerMemoryPanel({ companyId }) {
  const { can } = useAgentPermissions();
  const { addNotification } = useNotification();
  const qc = useQueryClient();
  const canView = can("agents_view");
  const canManage = can("agents_manage");
  const [showInactive, setShowInactive] = useState(false);
  const [form, setForm] = useState({ memory_class: "VERIFIED_CUSTOMER_FACT", content: "", source_type: "TECHNICIAN" });

  const queryKey = ["agent-memory", companyId, showInactive];
  const { data: notes = [], isLoading, isError, error } = useQuery({
    queryKey,
    queryFn: () => agentMemoryApi.list(companyId, showInactive),
    enabled: !!companyId && canView,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["agent-memory", companyId] });
  const onError = (err) => addNotification(detailText(err?.response?.data?.detail, "הפעולה נכשלה"), "error");

  const createMutation = useMutation({
    mutationFn: () => agentMemoryApi.create({
      company_id: companyId,
      memory_class: form.memory_class,
      kind: kindFor(form.memory_class),
      content: form.content.trim(),
      source_type: form.source_type,
    }),
    onSuccess: () => {
      addNotification("ההערה נשמרה בזיכרון הלקוח", "success");
      setForm((f) => ({ ...f, content: "" }));
      refresh();
    },
    onError,
  });
  const verifyMutation = useMutation({
    mutationFn: (note) => agentMemoryApi.verify(companyId, note.id),
    onSuccess: () => { addNotification("ההערה אומתה", "success"); refresh(); },
    onError,
  });
  const correctMutation = useMutation({
    mutationFn: ({ note, content }) => agentMemoryApi.correct(companyId, note.id, content),
    onError,
  });
  const deprecateMutation = useMutation({
    mutationFn: (note) => agentMemoryApi.deprecate(companyId, note.id),
    onSuccess: () => { addNotification("ההערה הוצאה משימוש", "info"); refresh(); },
    onError,
  });
  const deleteMutation = useMutation({
    mutationFn: (note) => agentMemoryApi.remove(companyId, note.id),
    onSuccess: () => { addNotification("ההערה נמחקה", "info"); refresh(); },
    onError,
  });

  if (!canView) return null;

  const handleCorrect = (note, content, done) =>
    correctMutation.mutate({ note, content }, {
      onSuccess: () => { addNotification("התיקון נשמר", "success"); done(); refresh(); },
    });
  const handleDelete = (note) => {
    if (window.confirm("למחוק את ההערה? התוכן יימחק ולא ניתן לשחזר.")) deleteMutation.mutate(note);
  };

  return (
    <section className="bg-white border rounded-xl shadow-sm p-5 space-y-4" aria-label="זיכרון לקוח">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="font-semibold text-gray-700">זיכרון לקוח ל-AI</h2>
          <p className="text-xs text-gray-500">
            הערות שהוקלטו על הלקוח הזה בלבד. הערה שלא אומתה מסומנת ככזו ואינה נחשבת עובדה. אין לשמור כאן סיסמאות, מפתחות או טוקנים.
          </p>
        </div>
        <label className="flex items-center gap-1 text-xs text-gray-500 shrink-0">
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          הצג גם שהוצאו משימוש
        </label>
      </div>

      {isLoading && <p className="text-sm text-gray-400">טוען...</p>}
      {isError && <p className="text-sm text-red-600">{detailText(error?.response?.data?.detail, "שגיאה בטעינת זיכרון הלקוח")}</p>}
      {!isLoading && !isError && notes.length === 0 && <p className="text-sm text-gray-400">אין עדיין הערות זיכרון ללקוח זה.</p>}

      <ul className="space-y-2">
        {notes.map((note) => (
          <NoteRow
            key={note.id}
            note={note}
            canManage={canManage}
            verifyBusy={verifyMutation.isPending && verifyMutation.variables?.id === note.id}
            correctBusy={correctMutation.isPending && correctMutation.variables?.note?.id === note.id}
            deprecateBusy={deprecateMutation.isPending && deprecateMutation.variables?.id === note.id}
            deleteBusy={deleteMutation.isPending && deleteMutation.variables?.id === note.id}
            onVerify={(n) => verifyMutation.mutate(n)}
            onCorrect={handleCorrect}
            onDeprecate={(n) => deprecateMutation.mutate(n)}
            onDelete={handleDelete}
          />
        ))}
      </ul>

      {canManage && (
        <form
          className="border-t pt-4 space-y-2"
          onSubmit={(e) => { e.preventDefault(); if (form.content.trim()) createMutation.mutate(); }}
        >
          <div className="flex flex-wrap gap-2">
            <select
              aria-label="סוג הערה"
              value={form.memory_class}
              onChange={(e) => setForm((f) => ({ ...f, memory_class: e.target.value }))}
              className="border rounded-lg px-2 py-1 text-xs bg-white"
            >
              {ADDABLE_CLASSES.map((c) => <option key={c} value={c}>{CLASS_LABELS[c]}</option>)}
            </select>
            <select
              aria-label="מקור ההערה"
              value={form.source_type}
              onChange={(e) => setForm((f) => ({ ...f, source_type: e.target.value }))}
              className="border rounded-lg px-2 py-1 text-xs bg-white"
            >
              <option value="TECHNICIAN">ידע של טכנאי</option>
              <option value="CUSTOMER_INSTRUCTION">הוראה מפורשת של הלקוח</option>
            </select>
          </div>
          <textarea
            rows={2}
            dir="auto"
            placeholder="למשל: הלקוח משתמש ב-Fortinet ולא ב-Cisco"
            aria-label="הערה חדשה"
            value={form.content}
            onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
            className="w-full border rounded-lg px-3 py-2 text-sm"
          />
          <button
            type="submit"
            disabled={createMutation.isPending || !form.content.trim()}
            className="bg-brand-600 hover:bg-brand-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50"
          >
            {createMutation.isPending ? "שומר..." : "הוסף הערה"}
          </button>
        </form>
      )}
    </section>
  );
}
